import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updatePublicFunctions, updateAdminFunctions, selectChannelById, fetchAvailableFunctionDefinitions } from '../../features/account/channels.slice';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Checkbox, FormControlLabel, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Typography, Box, TextField, InputAdornment, Chip, 
  ToggleButton, ToggleButtonGroup, Collapse, IconButton,
  Tooltip, Badge, Tabs, Tab, Switch, Fade, Grid,
  Card, CardContent, Divider, alpha, useTheme, useMediaQuery
} from '@mui/material';
import { RootState } from '../../store';
import { enqueueSnackbar } from 'notistack';
import { AppDispatch } from '../../store';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CategoryIcon from '@mui/icons-material/Category';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import FilterListIcon from '@mui/icons-material/FilterList';

// Category icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MessageIcon from '@mui/icons-material/Message';
import ContactsIcon from '@mui/icons-material/Contacts';
import ChatIcon from '@mui/icons-material/Chat';
import ImageIcon from '@mui/icons-material/Image';
import NoteIcon from '@mui/icons-material/Note';
import FunctionsIcon from '@mui/icons-material/Functions';

const categoryIcons: { [key: string]: React.ReactElement } = {
  boards: <DashboardIcon />,
  calendar: <CalendarTodayIcon />,
  communication: <MessageIcon />,
  contacts: <ContactsIcon />,
  conversations: <ChatIcon />,
  media: <ImageIcon />,
  notes: <NoteIcon />,
  utility: <FunctionsIcon />,
};

interface FunctionDefinition {
  name: string;
  friendly_name: string;
  short_description: string;
  description: string;
  category: string;
  is_admin_only: boolean;
  is_default_public: boolean;
  icon?: string;
}

interface FunctionPermissionDialogProps {
  open: boolean;
  onClose: () => void;
  channelId: string;
}

interface UpdateResult {
  success: boolean;
  error?: string;
}

const FunctionPermissionDialog: React.FC<FunctionPermissionDialogProps> = ({
  open, onClose, channelId
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch<AppDispatch>();
  const channel = useSelector((state: RootState) => selectChannelById(state, channelId));
  const [publicFunctions, setPublicFunctions] = useState<string[]>([]);
  const [adminFunctions, setAdminFunctions] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [functions, setFunctions] = useState<FunctionDefinition[]>([]);
  const [isLoadingFunctions, setIsLoadingFunctions] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [expandedCards, setExpandedCards] = useState<{ [key: string]: boolean }>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (channel) {
      setPublicFunctions(channel.public_functions || []);
      setAdminFunctions(channel.admin_functions || []);
    }
  }, [channel]);

  useEffect(() => {
    const loadFunctionDefinitions = async () => {
      setIsLoadingFunctions(true);
      try {
        const result = await dispatch(fetchAvailableFunctionDefinitions()).unwrap();
        if (result.success && result.functions) {
          setFunctions(result.functions);
        }
      } catch (error) {
        console.error('Failed to load function definitions:', error);
        setFunctions([]);
      } finally {
        setIsLoadingFunctions(false);
      }
    };

    if (open) {
      loadFunctionDefinitions();
    }
  }, [open, dispatch]);

  // Track changes
  useEffect(() => {
    const initialPublic = channel?.public_functions || [];
    const initialAdmin = channel?.admin_functions || [];
    const publicChanged = JSON.stringify([...publicFunctions].sort()) !== JSON.stringify([...initialPublic].sort());
    const adminChanged = JSON.stringify([...adminFunctions].sort()) !== JSON.stringify([...initialAdmin].sort());
    setHasChanges(publicChanged || adminChanged);
  }, [publicFunctions, adminFunctions, channel]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = ['all', ...new Set(functions.map(f => f.category))];
    return cats;
  }, [functions]);

  // Filter functions based on search and category
  const filteredFunctions = useMemo(() => {
    return functions.filter(func => {
      const matchesSearch = searchQuery === '' || 
        func.friendly_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        func.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        func.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || func.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [functions, searchQuery, selectedCategory]);

  // Group functions by category for grid view
  const groupedFunctions = useMemo(() => {
    if (selectedCategory !== 'all') {
      return { [selectedCategory]: filteredFunctions };
    }
    return filteredFunctions.reduce((acc, func) => {
      if (!acc[func.category]) {
        acc[func.category] = [];
      }
      acc[func.category].push(func);
      return acc;
    }, {} as { [key: string]: FunctionDefinition[] });
  }, [filteredFunctions, selectedCategory]);

  const handleToggle = (funcName: string, isPublic: boolean) => {
    if (isPublic && functions.find(f => f.name === funcName)?.is_admin_only) {
      enqueueSnackbar('This function cannot be set as public.', { variant: 'warning' });
      return;
    }
    const updateFunctions = isPublic ? setPublicFunctions : setAdminFunctions;
    updateFunctions(prev =>
      prev.includes(funcName) ? prev.filter(f => f !== funcName) : [...prev, funcName]
    );
  };

  const handleBulkToggle = (category: string, isPublic: boolean, enable: boolean) => {
    const categoryFunctions = category === 'all' 
      ? functions 
      : functions.filter(f => f.category === category);
    
    const updateFunctions = isPublic ? setPublicFunctions : setAdminFunctions;
    const eligibleFunctions = categoryFunctions.filter(f => !isPublic || !f.is_admin_only).map(f => f.name);
    
    if (enable) {
      updateFunctions(prev => [...new Set([...prev, ...eligibleFunctions])]);
    } else {
      updateFunctions(prev => prev.filter(f => !eligibleFunctions.includes(f)));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await Promise.all([
        dispatch(updatePublicFunctions({ channelId, publicFunctions })).unwrap(),
        dispatch(updateAdminFunctions({ channelId, adminFunctions })).unwrap(),
      ]);
      enqueueSnackbar('Functions updated successfully!', { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar('An error occurred while updating functions', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const renderFunctionCard = (func: FunctionDefinition) => {
    const isPublic = publicFunctions.includes(func.name);
    const isAdmin = adminFunctions.includes(func.name);
    const isExpanded = expandedCards[func.name];

    return (
      <Card 
        key={func.name}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.3s ease',
          border: `1px solid ${theme.palette.divider}`,
          '&:hover': {
            boxShadow: theme.shadows[4],
            borderColor: theme.palette.primary.light,
          },
          ...(isPublic && {
            borderColor: theme.palette.success.light,
            backgroundColor: alpha(theme.palette.success.main, 0.02),
          }),
          ...(isAdmin && !isPublic && {
            borderColor: theme.palette.warning.light,
            backgroundColor: alpha(theme.palette.warning.main, 0.02),
          }),
        }}
      >
        <CardContent sx={{ flexGrow: 1, pb: 1, px: { xs: 2, sm: 2 } }}>
          <Box display="flex" alignItems="flex-start" gap={2} mb={1}>
            {/* Function Icon in White Bubble */}
            {func.icon && (
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  backgroundColor: '#ffffff',
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: theme.shadows[1],
                }}
              >
                <Box
                  component="img"
                  src={func.icon}
                  alt=""
                  sx={{
                    width: 30,
                    height: 30,
                    objectFit: 'contain',
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </Box>
            )}
            
            <Box flex={1}>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                <Box flex={1}>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                  >
                    {func.friendly_name}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    component="div" 
                    gutterBottom
                    sx={{ 
                      display: { xs: 'none', sm: 'block' },
                      wordBreak: 'break-word'
                    }}
                  >
                    {func.name}
                  </Typography>
                </Box>
                {func.is_admin_only && (
                  <Tooltip title="Admin only - Cannot be made public">
                    <LockIcon color="warning" fontSize="small" />
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mb: 1 }}
          >
            {func.short_description}
          </Typography>
          
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Typography variant="body2" color="text.secondary" paragraph>
              {func.description}
            </Typography>
          </Collapse>
          
          {func.description !== func.short_description && (
            <Button
              size="small"
              onClick={() => setExpandedCards(prev => ({ ...prev, [func.name]: !prev[func.name] }))}
              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ ml: -1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </Button>
          )}
        </CardContent>
        
        <Divider />
        
        <Box sx={{ p: 2, pt: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isPublic}
                    onChange={() => handleToggle(func.name, true)}
                    disabled={func.is_admin_only}
                    color="success"
                    size="small"
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <PublicIcon fontSize="small" />
                    <Typography variant="body2">Public</Typography>
                  </Box>
                }
                sx={{ m: 0 }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isAdmin}
                    onChange={() => handleToggle(func.name, false)}
                    color="warning"
                    size="small"
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <AdminPanelSettingsIcon fontSize="small" />
                    <Typography variant="body2">Admin</Typography>
                  </Box>
                }
                sx={{ m: 0 }}
              />
            </Grid>
          </Grid>
        </Box>
      </Card>
    );
  };

  const renderListView = () => (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ minWidth: { xs: 500, sm: 'auto' } }}>
          <TableHead>
            <TableRow>
              <TableCell>Function</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Category</TableCell>
              <TableCell align="center">Public</TableCell>
              <TableCell align="center">Admin</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFunctions.map((func) => {
              const isPublic = publicFunctions.includes(func.name);
              const isAdmin = adminFunctions.includes(func.name);
              
              return (
                <TableRow 
                  key={func.name}
                  sx={{
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
                    ...(isPublic && { backgroundColor: alpha(theme.palette.success.main, 0.02) }),
                    ...(isAdmin && !isPublic && { backgroundColor: alpha(theme.palette.warning.main, 0.02) }),
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      {/* Function Icon in White Bubble for List View */}
                      {func.icon && (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: '#ffffff',
                            border: `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            component="img"
                            src={func.icon}
                            alt=""
                            sx={{
                              width: 24,
                              height: 24,
                              objectFit: 'contain',
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        </Box>
                      )}
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                          {func.friendly_name}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            display: { xs: 'none', sm: 'block' },
                            fontSize: { xs: '0.75rem', sm: '0.75rem' }
                          }}
                        >
                          {func.short_description}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Chip 
                      icon={categoryIcons[func.category] || <CategoryIcon />}
                      label={func.category} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={isPublic}
                      onChange={() => handleToggle(func.name, true)}
                      disabled={func.is_admin_only}
                      color="success"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={isAdmin}
                      onChange={() => handleToggle(func.name, false)}
                      color="warning"
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { 
          height: { xs: '100%', sm: '90vh' },
          margin: { xs: 0, sm: 2 },
          maxHeight: { xs: '100%', sm: 'calc(100% - 32px)' },
          width: { xs: '100%', sm: 'auto' },
          maxWidth: { xs: '100%', sm: 'lg' },
          borderRadius: { xs: 0, sm: 1 }
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography 
            variant="h5" 
            component="h2"
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
          >
            Function Permissions
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {hasChanges && (
              <Chip 
                label="Unsaved" 
                color="warning" 
                size="small" 
                variant="outlined"
                sx={{ display: { xs: 'none', sm: 'flex' } }}
              />
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
        <Grid container spacing={{ xs: 1, sm: 2 }} alignItems="stretch">
          <Grid item xs={12} sm={5} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search functions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={7} md={6}>
            <Box display="flex" alignItems="center" gap={1} sx={{ overflowX: 'auto' }}>
              <FilterListIcon color="action" sx={{ display: { xs: 'none', sm: 'block' } }} />
              <Tabs
                value={selectedCategory}
                onChange={(_, value) => setSelectedCategory(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ 
                  flexGrow: 1,
                  '& .MuiTab-root': {
                    minWidth: { xs: 'auto', sm: 120 },
                    px: { xs: 1, sm: 2 }
                  }
                }}
              >
                <Tab label="All" value="all" />
                {categories.filter(c => c !== 'all').map(category => (
                  <Tab 
                    key={category}
                    label={category}
                    value={category}
                    icon={categoryIcons[category] || <CategoryIcon />}
                    iconPosition="start"
                    sx={{
                      '& .MuiTab-iconWrapper': {
                        display: { xs: 'none', sm: 'block' }
                      }
                    }}
                  />
                ))}
              </Tabs>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={12} md={2}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, value) => value && setViewMode(value)}
              size="small"
              fullWidth
              sx={{ justifyContent: { xs: 'center', md: 'flex-start' } }}
            >
              <ToggleButton value="grid">
                <GridViewIcon />
              </ToggleButton>
              <ToggleButton value="list">
                <ViewListIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
        
        {selectedCategory === 'all' && viewMode === 'grid' && (
          <Box display="flex" gap={1} mt={2} flexWrap="wrap" sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleBulkToggle('all', true, true)}
              startIcon={<PublicIcon />}
              color="success"
            >
              Enable all public
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleBulkToggle('all', true, false)}
              color="inherit"
            >
              Disable all public
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleBulkToggle('all', false, true)}
              startIcon={<AdminPanelSettingsIcon />}
              color="warning"
            >
              Enable all admin
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleBulkToggle('all', false, false)}
              color="inherit"
            >
              Disable all admin
            </Button>
          </Box>
        )}
      </Box>
      
      <DialogContent sx={{ pt: 0, px: { xs: 2, sm: 3 } }}>
        {isLoadingFunctions ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={{ xs: 200, sm: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Fade in={!isLoadingFunctions}>
            <Box>
              {viewMode === 'grid' ? (
                <Box>
                  {Object.entries(groupedFunctions).map(([category, funcs]) => (
                    <Box key={category} mb={{ xs: 3, sm: 4 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            {categoryIcons[category] || <CategoryIcon />}
                          </Box>
                          <Typography 
                            variant="h6" 
                            component="h3"
                            sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                          >
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </Typography>
                          <Chip label={funcs.length} size="small" />
                        </Box>
                        {selectedCategory !== 'all' && (
                          <Box display="flex" gap={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                            <Button
                              size="small"
                              onClick={() => handleBulkToggle(category, true, true)}
                              startIcon={<PublicIcon />}
                            >
                              All Public
                            </Button>
                            <Button
                              size="small"
                              onClick={() => handleBulkToggle(category, false, true)}
                              startIcon={<AdminPanelSettingsIcon />}
                            >
                              All Admin
                            </Button>
                          </Box>
                        )}
                      </Box>
                      <Grid container spacing={{ xs: 1, sm: 2 }}>
                        {funcs.map(func => (
                          <Grid item xs={12} sm={6} lg={4} key={func.name}>
                            {renderFunctionCard(func)}
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ))}
                </Box>
              ) : (
                renderListView()
              )}
              
              {filteredFunctions.length === 0 && (
                <Box display="flex" flexDirection="column" alignItems="center" py={8}>
                  <SearchIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No functions found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your search or filters
                  </Typography>
                </Box>
              )}
            </Box>
          </Fade>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        px: { xs: 2, sm: 3 }, 
        py: 2, 
        borderTop: 1, 
        borderColor: 'divider',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        gap: { xs: 1, sm: 0 }
      }}>
        <Box display="flex" alignItems="center" gap={1} flex={1}>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            {publicFunctions.length} public • {adminFunctions.length} admin
          </Typography>
        </Box>
        <Button 
          onClick={onClose} 
          size="small"
          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          color="primary" 
          variant="contained"
          disabled={!hasChanges || isSaving}
          size="small"
          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          startIcon={isSaving ? <CircularProgress size={16} /> : hasChanges ? <CheckCircleIcon /> : null}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FunctionPermissionDialog;
